import React from 'react';
import { PiCheckLight, PiCopyLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';

/** Props for a value the user copies into another system. */
interface CopyableValueProps {
  /** Text to show and copy. */
  value: string;
  /** What the value is, used in the copy button's accessible name. */
  label: string;
  /** Extra classes for the outer row. */
  className?: string;
}

/**
 * Monospace value with a copy button, for codes, keys or DNS records that
 * should be pasted rather than retyped. The button confirms the copy for a
 * moment.
 *
 * @param props - Value, its label and optional classes.
 * @returns The value with its copy button.
 */
export const CopyableValue: React.FC<CopyableValueProps> = ({
  value,
  label,
  className,
}) => {
  const { t } = useI18n();
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  return (
    <div className={classes('flex min-w-0 items-center gap-1', className)}>
      <Text
        size="small"
        className="min-w-0 break-all font-mono"
        disableAutoLink
      >
        {value}
      </Text>
      <Button
        isIconOnly
        size="xs"
        variant="secondary"
        appearance="ghost"
        aria-label={t(
          isCopied
            ? 'shared.copyableValue.copied'
            : 'shared.copyableValue.copy',
          { label }
        )}
        onClick={() => void copyToClipboard(value)}
      >
        {isCopied ? (
          <PiCheckLight aria-hidden className="h-4 w-4" />
        ) : (
          <PiCopyLight aria-hidden className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
};
